/**
 * OIA MOCK MODE — activation flag (frontend-only, zero backend calls).
 *
 * LOCAL DEBUG ONLY, exactly like the OIO replay (features/oio/mock/mockMode.ts):
 * the appeal mock is a developer tool for showing and reviewing the officer's
 * flow with no backend (no model is reachable locally), never a user-facing
 * feature. It activates ONLY in a dev build (import.meta.env.DEV). In a
 * production build `?mock=oia` is inert and the mock machinery (with its
 * synthetic case and rendered order) is dead code the bundler drops, so the
 * mock page can never be reached in a deployed environment. The drafting
 * route imports the mock through a DEV-gated lazy import; this module is the
 * only piece of it the route imports statically, and it is a few lines.
 *
 *     http://localhost:5173/drafting?mock=oia
 *
 * The query string is read ONCE and latched for the page's lifetime: in-app
 * navigation can rewrite the URL, and the route swaps its whole tree on the
 * flag, which must never flap mid-session. A full page load with the param
 * is the only way in; a full page load without it is the only way out.
 */

/**
 * The synthetic, client-side-only case the mock runs under. Never sent to the
 * backend and never in the real case list: it lives only in the mock's own
 * ThreadContext override and in the per-case entries the mock writes to the
 * shared generation store (which it removes on restart and on unmount).
 */
export const OIA_MOCK_THREAD_ID = "mock-oia"

let latched: boolean | null = null

export function isOiaMock(): boolean {
  if (latched === null) {
    latched =
      import.meta.env.DEV &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mock") === "oia"
  }
  return latched
}

/**
 * Test-only escape hatch: vitest files share module state, so tests force the
 * latch instead of mutating window.location. Pass null to re-arm the lazy read.
 */
export function __setOiaMockForTests(value: boolean | null): void {
  latched = value
}
